import GetStartedBannerNew from '@Component/Banner/GetStartedBannerNew.tsx';
import { OptInAltinn3Banner } from '@Component/Banner/OptInAltinn3Banner.tsx';
import {
    ColumnsRender,
    CreateSalaryTableBody,
    SelectAllCheckbox,
} from '@Component/EmployeeTable/CreateSalaryTable.tsx';
import {
    EMPLOYEE_TABLE_COLUMNS_SETTING_KEY,
    EmployeeTableSettings,
    getDefaultColumns,
} from '@Component/EmployeeTable/EmployeeTableToolbar/EmployeeTableColumnSettings.tsx';
import { TableFilters } from '@Component/Filter/TableFilters.tsx';
import { Page } from '@Component/Page/Page';
import { LoadMoreTarget } from '@Component/Table/Table.utils.tsx';
import useLocalStorage from '@Hooks/storage.ts';
import { useLocalSearchParamState } from '@Hooks/useLocalSearchParamState.ts';
import { OrderByTypeCreateSalary, useTableSorting } from '@Hooks/useOrdering.ts';
import { usePageTitle } from '@Hooks/usePageTitle';
import { runTour } from '@Hooks/useTourMe';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext';
import { InsufficientAccessPage } from '@Page/InsufficientAccessPage/InsufficientAccessPage';
import { useEmployeeTableFilters } from '@Page/SalaryRun/hooks/useEmployeeTableFilters';
import { useSelectableEmployees } from '@Page/SalaryRun/hooks/useSelectableEmployees';
import { useSelectedPeriod } from '@Page/SalaryRun/hooks/useSelectedPeriod';
import {
    SALARY_PAY_IN_ADVANCE,
    useSuggestChangingPayInAdvance,
} from '@Page/SalaryRun/hooks/useSuggestChangingPayInAdvance.ts';
import { CreateSalaryModal } from '@Page/SalaryRun/Modals/CreateSalaryModal/CreateSalaryModal.tsx';
import { PayInAdvanceModal } from '@Page/SalaryRun/Modals/PayInAdvanceModal/PayInAdvanceModal.tsx';
import { ErrorBoundary } from '@tlx/sentry';
import {
    getContextId,
    PageActionButton,
    PageActions,
    PageActionsMenu,
    PageHeader,
    PageTitle,
    useSearchParam,
    useTableColumnsSetting,
} from '@tlx/astro-shared';
import {
    ActionsColumn,
    Body,
    Column,
    Group,
    GroupIcon,
    Header,
    Label,
    PopupMenuItemButton,
    Row,
    Table,
    TableTopBar,
    TipsIcon,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    YearAndMonthPickerPopover,
} from '@tlx/atlas';
import { BannerPersistence, useGetStartedBanner } from '@tlx/beehive-shared';
import { Tour } from '@tlx/tourme-core';
import { HalfTaxBanner, HolidayAllowanceBanner, toMonth } from '@tlx/tsk-shared';
import CreateSalaryTour from '@TourMe/CreateSalaryTour.json';
import { dateUtil } from '@Util/date';
import { isTSKCompanyOrFullPakke } from '@Util/isTSKCompany.ts';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import './CreateSalary.css';
import { useEmployees } from './hooks/useEmployees';
import { useSelectAllAPI } from './useSelectAllAPI.ts';

const MAX_PAST_YEARS = 20;
const MAX_FUTURE_YEARS = 1;

export function CreateSalaryRun() {
    const intl = useIntl();
    const pageTitle = intl.formatMessage({ id: 'text_new_wage_payment' });
    usePageTitle(pageTitle);
    const [openPayInAdvance, setOpenPayInAdvance] = useState(false);
    const [openCreateSalary, setOpenCreateSalary] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const contextId = getContextId();
    const {
        isPayInAdvance,
        isLoading: isLoadingContext,
        authLevelWage,
        userDepartmentId,
        hasDepartment,
        showDepartmentIsWageAdmin,
        hasDivision,
    } = useTskContext();
    const [selectedPeriod, setSelectedPeriod] = useSelectedPeriod(
        isPayInAdvance ? new Date() : dateUtil.addMonths(new Date(), -1),
    );
    const sorting = useTableSorting<OrderByTypeCreateSalary>();
    const navigate = useNavigate();
    const isHistorical = !!useSearchParam('isHistorical');
    const filters = useEmployeeTableFilters(selectedPeriod);
    const [departmentFilter, setDepartmentFilter] = useLocalSearchParamState('departmentIds');
    const relationships = useEmployees({
        year: selectedPeriod.getFullYear(),
        month: selectedPeriod.getMonth() + 1,
        filters,
        sort: sorting.sort,
    });

    const selectable = useSelectAllAPI({
        year: selectedPeriod.getFullYear(),
        month: selectedPeriod.getMonth() + 1,
        filters,
    });

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const {
        state,
        allChecked,
        someChecked,
        selectAllState,
        onUncheckAll,
        onCheckAll,
        isRowChecked,
        onCheckRow,
        onChangeEmployment,
    } = useSelectableEmployees(selectable);

    const [hidePayInAdvance] = useLocalStorage('hide_pay_in_advance', false);

    const createSalaryBannerInfo = useGetStartedBanner('CreateSalaryBanner', BannerPersistence.Dismissible);

    const defaultColumns = getDefaultColumns(intl, showDepartmentIsWageAdmin, hasDivision);

    const tableColumnSettings = useTableColumnsSetting(
        EMPLOYEE_TABLE_COLUMNS_SETTING_KEY,
        defaultColumns.map((column) => column.id),
    );

    const columnsHeader = defaultColumns
        .filter((column) => tableColumnSettings.includes(column.id))
        .map((column) => {
            if (column.id === 'NUMBER') {
                return {
                    ...column,
                    label: intl.formatMessage({ id: 'th_number_short' }),
                };
            }
            return column;
        })
        .sort((a, b) => {
            const firstColumnIndex = tableColumnSettings.indexOf(a.id);
            const secondColumnIndex = tableColumnSettings.indexOf(b.id);
            return firstColumnIndex - secondColumnIndex;
        });

    const suggestChangePayInAdvance = useSuggestChangingPayInAdvance(selectedPeriod);

    const { error } = relationships;

    if (isLoadingContext) {
        //Don't show anything while waiting for context
        return null;
    } else {
        if (authLevelWage < AUTH_LEVEL_DEPARTMENT) {
            return <InsufficientAccessPage />;
        }
    }

    if (error?.details?.status === 422 || error?.details?.status === 404) {
        // Handles wrong parameters
        // You get a 422 if the period/filter is wrong and 404 if the ordering is wrong
        navigate('?contextId=' + contextId);
    }

    if (error?.details?.status === 401 || error?.details?.status === 403) {
        // Don't show anything if not logged in
        // 403 should probably not happen, but to avoid infinite loops just in case
        return null;
    }

    function runCreateSalaryTour() {
        runTour(CreateSalaryTour as Tour, {
            testMode: true,
            language: intl.locale,
        });
    }

    const isFullPakkeOrTSK = isTSKCompanyOrFullPakke();

    if (
        authLevelWage == AUTH_LEVEL_DEPARTMENT &&
        userDepartmentId != 0 &&
        hasDepartment &&
        departmentFilter != userDepartmentId.toString()
    ) {
        setDepartmentFilter(userDepartmentId.toString());
    }

    return (
        <Page className={'atl-pb-24 create-salary__page'}>
            <PageHeader className="create-salary__header">
                <PageTitle>
                    <FormattedMessage
                        id={isHistorical ? 'text_new_historical_wage_transaction' : 'text_new_wage_payment'}
                    />
                </PageTitle>
                <PageActions>
                    <PageActionButton
                        id={'create-wagetransaction-button'}
                        variant={'primary'}
                        disabled={state.selectedEmployees.size === 0}
                        data-testid={'create-wagetransaction-button'}
                        onClick={() => {
                            if (state.selectedEmployees.size === 0) {
                                setShowTooltip(true);
                                return;
                            }
                            if (
                                !hidePayInAdvance &&
                                suggestChangePayInAdvance !== SALARY_PAY_IN_ADVANCE.SUGGEST_NOTHING
                            ) {
                                setOpenPayInAdvance(true);
                            } else {
                                setOpenCreateSalary(true);
                            }
                        }}
                    >
                        <Tooltip open={showTooltip}>
                            <TooltipTrigger
                                onMouseEnter={() => setShowTooltip(state.selectedEmployees.size === 0)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <span>
                                    <FormattedMessage id={'tsk_text_create_salary_payment'} />
                                </span>
                            </TooltipTrigger>
                            {state.selectedEmployees.size == 0 && (
                                <TooltipContent>
                                    <FormattedMessage id={'tsk_create_salary_payment_need_selection_tooltip'} />
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </PageActionButton>
                    <PageActionsMenu>
                        <PopupMenuItemButton data-testid="run-tour-me" onClick={runCreateSalaryTour}>
                            <TipsIcon />
                            <FormattedMessage id={'text_what_is_new'} />
                        </PopupMenuItemButton>
                        {isFullPakkeOrTSK ? (
                            <PopupMenuItemButton
                                onClick={() => window.open('/page/salary/stories/index.html', '_blank')}
                                data-testid="stories"
                            >
                                <GroupIcon />
                                <FormattedMessage id={'tsk_show_stories'} />
                            </PopupMenuItemButton>
                        ) : (
                            <></>
                        )}
                    </PageActionsMenu>
                </PageActions>
            </PageHeader>
            {openPayInAdvance && (
                <PayInAdvanceModal
                    suggestChangePayInAdvance={suggestChangePayInAdvance}
                    handleClose={() => {
                        setOpenPayInAdvance(false);
                        setOpenCreateSalary(true);
                    }}
                />
            )}
            {openCreateSalary && (
                <CreateSalaryModal
                    handleClose={setOpenCreateSalary}
                    defaultPeriod={selectedPeriod}
                    employmentIds={[...state.selectedEmployees.values()]}
                />
            )}
            <OptInAltinn3Banner />
            {/* TRIP-60608: temporarily disabled while the Altinn-3 opt-in banner is shown. Restore when that banner is removed. - Agent */}
            {/* eslint-disable-next-line no-constant-binary-expression */}
            {false && <GetStartedBannerNew createSalaryBannerInfo={createSalaryBannerInfo} />}
            <ErrorBoundary>
                <HolidayAllowanceBanner selectedMonth={toMonth(selectedPeriod.getMonth())} />
            </ErrorBoundary>
            <div className="atl-mt-16">
                <HalfTaxBanner selectedMonth={toMonth(selectedPeriod.getMonth())} />
            </div>
            <Group className={'atl-pb-24 create-salary__period atl-bg-bg'}>
                <Label>
                    <FormattedMessage id="text_period" />
                </Label>
                <YearAndMonthPickerPopover
                    selectedDate={selectedPeriod}
                    onChange={(newDate) => {
                        onUncheckAll();
                        setSelectedPeriod(newDate);
                    }}
                    pastYears={MAX_PAST_YEARS}
                    futureYears={MAX_FUTURE_YEARS}
                />
            </Group>
            <TableTopBar>
                <TableFilters filterState={filters} tableType={'createSalary'} />
                <EmployeeTableSettings />
            </TableTopBar>
            <Table
                className={'create-salary__table'}
                selectedRows={Object.keys(state.selectedEmployees).map(String)}
                onSortChange={sorting.onSortChange}
                sort={sorting.sort}
                density="compact"
            >
                <Header>
                    <Row className={'create-salary__table-header'}>
                        <ActionsColumn>
                            <SelectAllCheckbox
                                isCheckedAll={allChecked}
                                selectAllState={selectAllState}
                                onCheckAll={onCheckAll}
                                someChecked={someChecked}
                            />
                        </ActionsColumn>
                        <Column width={'2fr'} sortable id={'FIRSTNAME'}>
                            <FormattedMessage id="text_employees" />
                        </Column>
                        <ColumnsRender columns={columnsHeader} />
                    </Row>
                </Header>
                <Body>
                    <CreateSalaryTableBody
                        relationships={relationships}
                        isRowChecked={isRowChecked}
                        onCheckRow={onCheckRow}
                        onChangeEmployment={onChangeEmployment}
                        filters={filters}
                    />
                </Body>
            </Table>
            <LoadMoreTarget
                hasMore={relationships.hasMore}
                onLoadMore={relationships.loadMore}
                scrollContainerRef={scrollContainerRef}
            />
        </Page>
    );
}

const redirectUrl = () => {
    const contextId = getContextId();
    return '/execute/createWageTransaction?contextId=' + contextId;
};

CreateSalaryRun.redirectUrl = redirectUrl;
